import { type FC } from 'react';

import Pagination from 'molecules/Pagination';
import Section from 'molecules/Section';
import { splitSectionProps } from 'molecules/Section/config';

import ScrollFadeSection from 'components/Animations/ScrollFadeSection';

import StandardFeed from 'layout/Listing/components/StandardFeed';
import { ListingConfigProps } from 'layout/Listing/config';
import usePagination from 'layout/Listing/hooks/usePagination';
import useSaveReturnUrl from 'layout/Listing/hooks/useSaveReturnUrl';
import VideosFilters from 'layout/Listing/variants/videos/components/Filters';
import VideosSearchBar from 'layout/Listing/variants/videos/components/SearchBar';
import useFilterVideos from 'layout/Listing/variants/videos/hooks/useFilteredVideos';
import useVideoFilterSearchParam from 'layout/Listing/variants/videos/hooks/useVideoFilterSearchParam';

import { hasArrayValues } from 'utils/array/arrays';
import { mergeListings } from 'utils/array/mergeListings';
import { toArray } from 'utils/array/toArray';
import { getBuilderStateData } from 'utils/builder';
import useForceReloadOnBackToResources from 'utils/hooks/useForceReloadOnBackToResources';

import videosData from 'data/videos.json';

const PAGE_SIZE = 12;

type VideoItem = { data: VideoResource };

const VideosListing: FC<ListingConfigProps> = props => {
  const {
    sectionProps,
    rest: { newsletterCard, builderState, searchPlaceholder, headingTitle },
  } = splitSectionProps(props);

  const rawLatestData = getBuilderStateData('listingData', builderState);
  const latestData = toArray<VideoItem>(rawLatestData);

  const localData = toArray<VideoItem>(videosData);

  const videosList: VideoItem[] = hasArrayValues(latestData) ? mergeListings(latestData, localData) : localData;

  if (!hasArrayValues(videosList)) {
    console.warn('[VideosListing] No items to render – check builderState/listingData and videos.json');
  }

  useVideoFilterSearchParam();

  const filteredVideos = useFilterVideos(videosList).sort(
    (a, b) => new Date(b.data?.publishDate || 0).getTime() - new Date(a.data?.publishDate || 0).getTime(),
  );

  const { currentPage, paginatedItems, totalItems, handlePageChange } = usePagination({
    items: filteredVideos,
    pageSize: PAGE_SIZE,
  });

  useSaveReturnUrl('/videos');

  useForceReloadOnBackToResources();

  return (
    <Section {...sectionProps} darkMode={false}>
      <ScrollFadeSection delay={0.1} containPaint={false}>
        <div id="listing-section" className="flex flex-col gap-8">
          <div className="grid grid-cols-1 justify-center gap-8 sm:grid-cols-3 lg:grid-cols-2">
            <h2 className="font-spaceGrotesk text-display-md font-semibold text-gray-900 sm:max-lg:col-span-full">
              {headingTitle || 'All Videos'}
            </h2>
            <VideosFilters />
            <VideosSearchBar placeholder={searchPlaceholder} />
          </div>
          <StandardFeed posts={paginatedItems} newsletterCard={newsletterCard} />
          {totalItems > PAGE_SIZE && (
            <Pagination
              count={totalItems}
              pageSize={PAGE_SIZE}
              setPage={detail => handlePageChange(detail.page)}
              currentPage={currentPage}
            />
          )}
        </div>
      </ScrollFadeSection>
    </Section>
  );
};

export default VideosListing;
